import React from "react";
import styled from "styled-components";

import {
  brand_orange,
  brand_orange_deep,
  brand_yellow,
  narrowScreenMediaQuery,
} from "../../constants/styles";
import SearchableSelect from "../../components/SearchableSelect";

// The group picker. Grouping by Nama Materi Belajar Pajak is expressed as
// NAVIGATION rather than as page layout: picking a group simply presets the
// `learning_name` filter on the flat, paginated grid. That keeps one meaning for
// `?page=` and one meaning for `meta.total`, so a shared URL stays stable.
//
// A persistent rail on desktop; below the narrow breakpoint it collapses into the
// same SearchableSelect combobox the webinar filters use, because a 30-item
// vertical rail is unusable on a phone.

const Rail = styled.nav`
  align-self: start;
  position: sticky;
  top: 96px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
  padding: 8px;
  max-height: calc(100vh - 140px);
  overflow-y: auto;

  [data-theme="dark"] & {
    background: #17171e;
    border-color: #2b2b36;
  }

  @media ${narrowScreenMediaQuery} {
    display: none;
  }
`;

const RailHeading = styled.h2`
  margin: 0;
  padding: 8px 10px 10px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const RailList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

// A button, not a link: this sets a filter on the current page rather than
// navigating, and the URL is kept in sync by the hook.
const RailButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 10px;
  border: 0;
  border-radius: 9px;
  background: ${(props) => (props.$active ? "#fff4ec" : "transparent")};
  color: ${(props) => (props.$active ? brand_orange_deep : "#374151")};
  font-size: 13px;
  font-weight: ${(props) => (props.$active ? 800 : 600)};
  text-align: left;
  cursor: pointer;
  transition: background 150ms ease;

  &:hover {
    background: ${(props) => (props.$active ? "#ffe9d8" : "#f3f4f6")};
  }

  &:focus-visible {
    outline: 2px solid ${brand_orange};
    outline-offset: 1px;
  }

  [data-theme="dark"] & {
    background: ${(props) => (props.$active ? "#2a2118" : "transparent")};
    color: ${(props) => (props.$active ? brand_yellow : "#d1d5db")};

    &:hover {
      background: ${(props) => (props.$active ? "#3b2a1a" : "#1f2937")};
    }
  }
`;

const RailLabel = styled.span`
  /* Group names run long ("Rekaman Live Online Classes Brevet AB Batch XIII"),
   * so wrap rather than clip — the count must stay readable beside it.
   */
  overflow-wrap: anywhere;
`;

const RailCount = styled.span`
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const NarrowPicker = styled.div`
  display: none;

  @media ${narrowScreenMediaQuery} {
    display: block;
    margin-bottom: 16px;
  }
`;

const NarrowLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

function MateriSidebar({ options = [], value = "", onChange, total = 0 }) {
  const selectOptions = React.useMemo(
    () =>
      options.map((o) => ({
        value: o.slug,
        label: o.name,
        count: o.count,
      })),
    [options],
  );

  return (
    <>
      <NarrowPicker>
        <NarrowLabel htmlFor="materi-picker">Nama Materi Belajar</NarrowLabel>
        <SearchableSelect
          id="materi-picker"
          value={value}
          onChange={(next) => onChange(next)}
          options={selectOptions}
          allLabel={`Semua Materi${total ? ` (${total})` : ""}`}
          ariaLabel="Pilih Nama Materi Belajar"
          placeholder="Semua Materi"
          searchPlaceholder="Cari nama materi..."
        />
      </NarrowPicker>

      <Rail aria-label="Nama Materi Belajar Pajak">
        <RailHeading>Materi</RailHeading>
        <RailList>
          <li>
            <RailButton
              type="button"
              $active={!value}
              aria-current={!value ? "true" : undefined}
              onClick={() => onChange("")}
            >
              <RailLabel>Semua Materi</RailLabel>
              {total > 0 && <RailCount>{total}</RailCount>}
            </RailButton>
          </li>
          {options.map((option) => (
            <li key={option.slug}>
              <RailButton
                type="button"
                $active={value === option.slug}
                aria-current={value === option.slug ? "true" : undefined}
                onClick={() => onChange(option.slug)}
              >
                <RailLabel>{option.name}</RailLabel>
                <RailCount>{option.count}</RailCount>
              </RailButton>
            </li>
          ))}
        </RailList>
      </Rail>
    </>
  );
}

// Memoized: this rail renders one styled button per Nama Materi Belajar (30+ in
// production). Its props change only when the option list or the selected slug
// changes, so it should never re-render for anything happening in the search box.
export default React.memo(MateriSidebar);
